import { useState, useEffect, useRef } from "react";
import { useLang } from "../LangContext";
import { supabase } from "../supabase";

export default function FloorPlan({ projectId, readOnly = false }) {
  const { t } = useLang();
  const [imageUrl, setImageUrl] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    supabase.from("project_data").select("data").eq("project_id", projectId).eq("section", "floorplan")
      .then(({ data }) => {
        if (data && data[0]?.data) {
          const d = data[0].data;
          setImageUrl(d.url || d.base64 || null);
          setIsPdf(d.isPdf || false);
        }
      });
  }, [projectId]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileIsPdf = file.type === "application/pdf";
    if (!fileIsPdf && !file.type.startsWith("image/")) return;
    setSaving(true);
    e.target.value = "";

    try {
      const ext = fileIsPdf ? "pdf" : (file.name.split(".").pop() || "jpg");
      const path = `${projectId}/plan.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("floorplans")
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from("floorplans").getPublicUrl(path);

      setImageUrl(publicUrl);
      setIsPdf(fileIsPdf);
      setZoom(1);
      setPan({ x: 0, y: 0 });

      await supabase.from("project_data").upsert(
        { project_id: projectId, section: "floorplan", data: { url: publicUrl, isPdf: fileIsPdf }, updated_at: new Date().toISOString() },
        { onConflict: "project_id,section" }
      );
    } catch (err) {
      console.error("Upload error:", err);
      if (!file.type.startsWith("image/")) {
        setSaving(false);
        return;
      }
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result;
        setImageUrl(base64);
        setIsPdf(false);
        setZoom(1);
        setPan({ x: 0, y: 0 });
        await supabase.from("project_data").upsert(
          { project_id: projectId, section: "floorplan", data: { base64, isPdf: false }, updated_at: new Date().toISOString() },
          { onConflict: "project_id,section" }
        );
      };
      reader.readAsDataURL(file);
    }

    setSaving(false);
  };

  const onMouseDown = (e) => { setDragging(true); dragRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; };
  const onMouseMove = (e) => { if (!dragging || !dragRef.current) return; setPan({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y }); };
  const onMouseUp = () => setDragging(false);
  const onWheel = (e) => { e.preventDefault(); setZoom(z => Math.min(4, Math.max(0.5, z + (e.deltaY > 0 ? -0.1 : 0.1)))); };

  const touchRef = useRef({ lastDist: null, lastPan: null });

  const getDistance = (t1, t2) =>
    Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  const getMidpoint = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  const onTouchStart = (e) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      setDragging(true);
      dragRef.current = { x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y };
      touchRef.current = { lastDist: null, lastPan: null };
    } else if (e.touches.length === 2) {
      setDragging(false);
      touchRef.current = {
        lastDist: getDistance(e.touches[0], e.touches[1]),
        lastPan: getMidpoint(e.touches[0], e.touches[1]),
      };
    }
  };

  const onTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 1 && dragging && dragRef.current) {
      setPan({ x: e.touches[0].clientX - dragRef.current.x, y: e.touches[0].clientY - dragRef.current.y });
    } else if (e.touches.length === 2 && touchRef.current.lastDist !== null) {
      const newDist = getDistance(e.touches[0], e.touches[1]);
      const scale = newDist / touchRef.current.lastDist;
      setZoom(z => Math.min(4, Math.max(0.5, z * scale)));
      touchRef.current.lastDist = newDist;
    }
  };

  const onTouchEnd = (e) => {
    e.preventDefault();
    if (e.touches.length === 0) {
      setDragging(false);
      touchRef.current = { lastDist: null, lastPan: null };
    }
  };

  if (!imageUrl) return readOnly ? null : (
    <div style={{ marginBottom: 20 }}>
      <input ref={inputRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={handleFile} />
      <div onClick={() => inputRef.current.click()}
        style={{ height: "min(160px, 40vw)", border: "1px dashed #3A3830", borderRadius: 14, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, cursor: "pointer", background: "#141410" }}>
        <div style={{ fontSize: 32 }}>📐</div>
        <div style={{ fontSize: 13, color: "#7A7468" }}>Ajouter le plan du projet</div>
        <div style={{ fontSize: 11, color: "#3A3830" }}>PNG, JPG ou PDF</div>
      </div>
    </div>
  );

  // PDF display - inline iframe
  if (isPdf) return (
    <div style={{ marginBottom: 20 }}>
      {!readOnly && <input ref={inputRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={handleFile} />}
      <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", border: "1px solid #2A2924", background: "#141410" }}>
        <iframe
          src={imageUrl}
          title="Plan du projet"
          style={{ width: "100%", height: "min(600px, 80vw)", border: "none", display: "block" }}
        />
        {!readOnly && (
          <button onClick={() => inputRef.current.click()}
            style={{ position: "absolute", bottom: 12, right: 12, background: "rgba(15,14,12,0.85)", border: "1px solid #C9A96E60", borderRadius: 8, padding: "7px 14px", fontSize: 11, color: "#C9A96E", cursor: "pointer", fontFamily: "inherit" }}>
            {saving ? "..." : "✎ Remplacer"}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ marginBottom: 20 }}>
      {!readOnly && <input ref={inputRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={handleFile} />}
      <div
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} onWheel={onWheel}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        style={{ position: "relative", height: "min(480px, 75vw)", minHeight: 240, borderRadius: 14, overflow: "hidden", border: "1px solid #2A2924", background: "#141410", cursor: dragging ? "grabbing" : "grab", touchAction: "none" }}>
        <img src={imageUrl} alt="Plan" style={{ position: "absolute", top: "50%", left: "50%", transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${zoom})`, transformOrigin: "center", maxWidth: "100%", maxHeight: "100%", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: 12, right: 12, fontSize: 10, color: "#5A5450", background: "rgba(15,14,12,0.8)", padding: "3px 8px", borderRadius: 6 }}>Pincer pour zoomer · Glisser pour déplacer</div>
        <div style={{ position: "absolute", bottom: 12, left: 12, display: "flex", gap: 6 }}>
          {[{ l: "+", fn: () => setZoom(z => Math.min(4, z + 0.25)) }, { l: "−", fn: () => setZoom(z => Math.max(0.5, z - 0.25)) }, { l: "↺", fn: () => { setZoom(1); setPan({ x: 0, y: 0 }); } }].map(b => (
            <button key={b.l} onClick={b.fn} style={{ width: 36, height: 36, background: "rgba(15,14,12,0.85)", border: "1px solid #2A2924", borderRadius: 8, color: "#E8E2D8", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{b.l}</button>
          ))}
          <span style={{ fontSize: 10, color: "#7A7468", background: "rgba(15,14,12,0.85)", padding: "0 10px", borderRadius: 8, display: "flex", alignItems: "center" }}>{Math.round(zoom * 100)}%</span>
        </div>
        {!readOnly && (
          <button onClick={() => inputRef.current.click()} style={{ position: "absolute", bottom: 12, right: 12, background: "rgba(15,14,12,0.85)", border: "1px solid #C9A96E60", borderRadius: 8, padding: "7px 14px", fontSize: 11, color: "#C9A96E", cursor: "pointer", fontFamily: "inherit" }}>
            {saving ? t("uploading2") : t("replace")}
          </button>
        )}
      </div>
    </div>
  );
}
